import './popupUpsert.scss';

import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { confirm } from 'devextreme/ui/dialog';
import { Form, Button, Popup, ScrollView, DataGrid, Toast, LoadPanel, RadioGroup, CheckBox } from 'devextreme-react';
import { DataGridRef, Column } from 'devextreme-react/data-grid';
import { FormRef, Item, GroupItem } from 'devextreme-react/form';
import { Position, ToolbarItem } from 'devextreme-react/popup';
import { ClickEvent } from 'devextreme/ui/button';
import { useScreenSize } from '../../../../utils/media-query';

import { ATS_RapportoInterventoPersonaleEdit } from '..';
import { PopupPersonaleEditHandle } from './popupPersonaleEdit';

import useApiConfiguration from '../../../../apiConfig';
import {
    RapportiniApi,
    RapportiniDTO, RapportiniDTOToJSON,
    RapportiniUpdateDTOFromJSON,
    PersonaleRapportinoDTOFromJSON, PersonaleRapportinoDTOToJSON,
    DDTAttiviDTO
} from '../../../../data';

export type PopupEditHandle = {
    show: (id: number) => void
};

type Props = {
    onChange: () => void
};

type State = {
    id: number | undefined,
    visible: boolean
};

const PopupEdit: React.ForwardRefRenderFunction<PopupEditHandle, Props> = (props, ref) => {
    const formRef = useRef<FormRef>(null);
    const personaleGridRef = useRef<DataGridRef>(null);
    const popupPersonaleEditRef = useRef<PopupPersonaleEditHandle>(null);

    const apiConfig = useApiConfiguration();
    const rapportiniApi = new RapportiniApi(apiConfig);
    const { isLarge, isMedium, isSmall, isXSmall } = useScreenSize();

    const [state, setState] = useState<State>({ id: undefined, visible: false });
    const [toastConfig, setToastConfig] = useState({ isVisible: false, message: '' } as { isVisible: boolean, message: string });
    const [loading, setLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<RapportiniDTO>();
    const [ddtDataSource, setDdtDataSource] = useState<DDTAttiviDTO[]>([]);

    function hideInfo() {
        setLoading(false);
        setState({ id: undefined, visible: false });
        setDdtDataSource([]);
    }

    useImperativeHandle(ref, () => ({
        show(id) {
            setLoading(true);
            setState({ id: id, visible: true });
        }
    }));

    useEffect(() => {
        if (state.id) {
            rapportiniApi.getRapportinoById({ idRapportino: state.id })
                .then(function (data) {
                    if (data) {
                        setFormData({
                            ...data,
                            id: state.id
                        });

                        var api = new RapportiniApi(apiConfig);
                        api.getDDTAttivi({
                            clienteId: data.idCliente
                        }).then((res) => {
                            setDdtDataSource(res);
                            setLoading(false);
                        });
                    }
                })
                .catch(() => hideInfo());
        }
    }, [state.id, state.visible]);

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const update = (e: ClickEvent) => {
        const validate = formRef.current?.instance().validate();

        if (validate?.isValid) {
            if ((formData.personaleRapportino ?? []).length == 0) {
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    message: 'Inserire il personale'
                });
            } else {
                var json = RapportiniDTOToJSON(formData);
                var objUpd = RapportiniUpdateDTOFromJSON(json);

                rapportiniApi.updateRapportinoById({
                    id: state.id,
                    rapportiniUpdateDTO: objUpd
                })
                    .then(() => props.onChange())
                    .finally(() => hideInfo());
            }
        }
    };

    const openPersonale = (rowIdx: number) => {
        if (rowIdx == -1) {
            if (formData.personaleRapportino.length > 0) {
                var obj = formData.personaleRapportino.find((_, idx) => idx === 0);
                var json = JSON.stringify(PersonaleRapportinoDTOToJSON(obj));
                popupPersonaleEditRef.current.show(json, -1, formData.idCliente, false);
            } else
                popupPersonaleEditRef.current.show('', -1, formData.idCliente, false);
        } else {
            var obj = formData.personaleRapportino.find((_, idx) => idx === rowIdx);
            var json = JSON.stringify(PersonaleRapportinoDTOToJSON(obj));
            popupPersonaleEditRef.current.show(json, rowIdx, formData.idCliente, true);
        }
    };

    const upsertPersonale = (json: string, idx: number) => {
        const newObj = PersonaleRapportinoDTOFromJSON(JSON.parse(json));

        setFormData(prev => {
            const list = prev.personaleRapportino ?? [];
            if (idx === -1 || idx < 0 || idx >= list.length) {
                return {
                    ...prev,
                    personaleRapportino: [...list, newObj]
                };
            }

            const next = list.slice();
            next[idx] = newObj;
            return {
                ...prev,
                personaleRapportino: next
            }
        });
    };

    function checkDeletePersonale(rowIndex: number) {
        const result = confirm("Confermi l'eliminazione?", "Conferma");
        result.then((dialogRes) => {
            if (dialogRes)
                deletePersonale(rowIndex);
        });
    };

    function deletePersonale(rowIndex: number) {
        setFormData((prev) => ({
            ...prev,
            personaleRapportino: prev.personaleRapportino.filter((_, idx) => idx !== rowIndex)
        }));

        personaleGridRef.current?.instance().refresh();
    };

    return (
        <React.Fragment>
            <Popup
                enableBodyScroll={false}
                visible={state.visible}
                dragEnabled={false}
                hideOnOutsideClick
                showCloseButton showTitle
                fullScreen={(isSmall || isXSmall ? true : false)}
                title={'Modifica Rapporto M. 111'}
                container=".dx-viewport"
                height={"85%"}
                onHidden={hideInfo}
            >
                <Position at="center" my="center" of="" collision="fit" />

                <ToolbarItem widget='dxButton' toolbar='bottom' location='center'>
                    <Button
                        onClick={update}
                        icon="check"
                        text="Salva"
                        type='default'
                        stylingMode='contained'
                    />
                </ToolbarItem>

                {
                    loading ?
                        <LoadPanel style={{ zIndex: 9999 }} shading={true} shadingColor='rgba(0, 0, 0, 0.4)' position={'center'} visible={true} />
                        : (
                            <ScrollView className='rapportino-edit-scroll'>
                                <Form
                                    ref={formRef} formData={formData}
                                    labelMode='floating' labelLocation='left'
                                    colCount={(isLarge || isMedium) ? 2 : 1}
                                    style={{ padding: "10px" }}
                                >
                                    <Item visible={false} dataField='id' />
                                    <Item visible={false} dataField='tipoRapportino' />

                                    <Item colSpan={1} dataField='cliente' editorType='dxTextBox' editorOptions={{ disabled: true }} />

                                    <Item colSpan={1} itemType='empty' visible={(isLarge || isMedium)} />

                                    <Item colSpan={1} dataField="personaRiferimento" label={{ text: "Persona Riferimento" }} editorType="dxTextBox" isRequired={true} />

                                    <Item colSpan={1} dataField="luogo" label={{ text: "Luogo" }} editorType="dxTextBox" isRequired={true} />

                                    <GroupItem caption="Controllo Periodico" colSpan={(isLarge || isMedium) ? 2 : 1} colCount={4}>
                                        <Item colSpan={(isLarge || isMedium) ? 3 : 4}>
                                            <div className='custom-controlloperiodico'>
                                                <RadioGroup
                                                    items={["Trimestrale", "Semestrale", "Annuale"]}
                                                    layout='horizontal'
                                                    value={
                                                        formData?.isTrimestrale ? "Trimestrale"
                                                            : formData?.isSemestrale ? "Semestrale"
                                                                : formData?.isAnnuale ? "Annuale" : null
                                                    }
                                                    onValueChanged={(e) => {
                                                        const v = e.value;

                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            isTrimestrale: v === "Trimestrale",
                                                            isSemestrale: v === "Semestrale",
                                                            isAnnuale: v === "Annuale"
                                                        }));
                                                    }}
                                                />

                                                <CheckBox text="Controlli non distruttivi"
                                                    value={formData?.isControlliNonDistruttivi ?? false}
                                                    onValueChanged={(e) => handleChange("isControlliNonDistruttivi", e.value)}
                                                />

                                                <CheckBox text="Manutenzione Straordinaria"
                                                    value={formData?.isManutenzioneStraordinaria ?? false}
                                                    onValueChanged={(e) => handleChange('isManutenzioneStraordinaria', e.value)}
                                                />
                                            </div>
                                        </Item>
                                    </GroupItem>

                                    <GroupItem caption='Riferimento Ordine' colSpan={1} colCount={2}>
                                        <Item colSpan={1} dataField='riferimentoOrdine' label={{ text: "N." }} editorType='dxTextBox' />

                                        <Item colSpan={1} dataField="dataRiferimentoOrdine" label={{ text: "Del" }} editorType="dxDateBox"
                                            editorOptions={{
                                                type: "date",
                                                displayFormat: (isLarge || isMedium) ? "EEEE d MMMM yyyy" : "dd/MM/yyyy",
                                                openOnFieldClick: true
                                            }}
                                        />
                                    </GroupItem>

                                    <GroupItem caption='Materiali Impiegati' colSpan={1} colCount={2}>
                                        <Item colSpan={2} label={{ text: "DDT" }} dataField='riferimentoDDT'
                                            editorType='dxSelectBox' isRequired={formData?.isManutenzioneStraordinaria ?? false}
                                            editorOptions={{
                                                disabled: false,
                                                dataSource: ddtDataSource,
                                                displayExpr: 'descrizione',
                                                valueExpr: 'numero',
                                                searchEnabled: true,
                                                showClearButton: true,
                                                onValueChanged: ((e) => {
                                                    const obj = ddtDataSource.find(x => x.numero === e.value);
                                                    formData.dataRiferimentoDDT = obj?.data ?? null;
                                                })
                                            }}
                                        />
                                    </GroupItem>

                                    <Item colSpan={(isLarge || isMedium) ? 2 : 1} dataField="attrezzatureSpeciali" label={{ text: "Attrezzature Speciali utilizzate" }}
                                        editorType="dxTextArea" cssClass="dx-textarea-custom"
                                        editorOptions={{
                                            maxLength: 250,
                                            autoResizeEnabled: true
                                        }}
                                    />

                                    <Item colSpan={(isLarge || isMedium) ? 2 : 1}>
                                        <div style={{ display: 'flex', justifyContent: 'end', marginTop: '10px', marginBottom: "10px" }}>
                                            <Button icon="add" text='Aggiungi Personale' stylingMode='text' onClick={() => openPersonale(-1)} />
                                        </div>

                                        <DataGrid
                                            className='grid theme-dependent' ref={personaleGridRef}
                                            dataSource={formData?.personaleRapportino}
                                            rowAlternationEnabled defaultFocusedRowIndex={0} noDataText='Nessun personale caricato'
                                        >
                                            <Column
                                                width={100}
                                                cellRender={(cellData) => (
                                                    <div style={{ display: "flex" }}>
                                                        <Button icon="edit" hint="Modifica" stylingMode='text' onClick={() => openPersonale(cellData.rowIndex)} />
                                                        <Button icon="trash" hint="Elimina" stylingMode='text' onClick={() => checkDeletePersonale(cellData.rowIndex)} />
                                                    </div>
                                                )}
                                            />
                                            <Column dataField='dataIntervento' caption='Data' />
                                            <Column dataField="personale" />
                                            <Column dataField="commessa" />
                                            <Column dataField="oreTotali" />
                                        </DataGrid>
                                    </Item>
                                </Form>
                            </ScrollView>
                        )
                }
            </Popup>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'center', my: 'center', of: window, offset: { x: 0, y: 0 } }}
                type='error' displayTime={3000}
                onHidden={onHidingToast}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 400, to: 0 } }}
            />

            <ATS_RapportoInterventoPersonaleEdit ref={popupPersonaleEditRef} onChange={upsertPersonale} />
        </React.Fragment>
    );
};

export default React.forwardRef(PopupEdit);